import React, { useState, useMemo, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import ModelIcon from './ModelIcon';
import TextDiffusionAnimation from './TextDiffusionAnimation';
import exportDeepResearchPdf from '../utils/deepResearchPdfExport';
import StreamingMarkdownRenderer from './StreamingMarkdownRenderer';
import { extractSourcesFromResponse } from '../utils/sourceExtractor';
import { openExternalUrl } from '../utils/urlSecurity';
import { processCodeBlocks } from '../utils/codeBlockProcessor';
import CodeBlockWithExecution from './CodeBlockWithExecution';
import useSupportedLanguages from '../hooks/useSupportedLanguages';
import ReactKatex from '@pkasila/react-katex';
import 'katex/dist/katex.min.css';
import { useTranslation } from '../contexts/TranslationContext';
import { downloadGeneratedVideo } from '../services/videoService';
import kokoroTTSService from '../services/kokoroTTSService';
import { THINKING_PREVIEW_TIMER_ENABLED, isReasoningEffortAboveMedium } from '../config/modelConfig';
import { copyToClipboard, createSharedChat, getSharedChatUrl } from '../services/shareService';

// Helper function to parse and render LaTeX
const renderLatex = (latex, displayMode, keyPrefix = 'latex') => (
  <ReactKatex
    key={`${keyPrefix}-${latex.length}-${displayMode}`}
    displayMode={displayMode}
    throwOnError={false}
    strict={false}
  >
    {latex}
  </ReactKatex>
);

const extractThinkingFromContent = (content = '') => {
  if (!content) {
    return { mainContent: '', thinkingContent: null };
  }

  const thinkingRegex = /<think>([\s\S]*?)<\/think>/gi;
  const matches = [...content.matchAll(thinkingRegex)];

  if (matches.length === 0) {
    return { mainContent: content, thinkingContent: null };
  }

  const thinkingContent = matches
    .map((match) => match[1]?.trim())
    .filter(Boolean)
    .join('\n\n');

  return {
    mainContent: content.replace(thinkingRegex, '').trim(),
    thinkingContent: thinkingContent || null
  };
};

// Format markdown text including bold, italic, bullet points and code blocks
const robustFormatContent = (content, isLanguageExecutable = null, supportedLanguages = [], theme = {}) => {
  if (!content) return '';

  const { mainContent, thinkingContent } = extractThinkingFromContent(content);

  // If we have thinking content, return an object with both processed contents
  if (thinkingContent) {
    return {
      main: processText(mainContent, true, isLanguageExecutable, supportedLanguages, theme),
      thinking: processText(thinkingContent, true, isLanguageExecutable, supportedLanguages, theme)
    };
  }

  // Otherwise, just process the content normally
  return processText(mainContent, true, isLanguageExecutable, supportedLanguages, theme);
};

// Convert markdown syntax to HTML using a more straightforward approach
const processText = (text, enableCodeExecution = true, isLanguageExecutable = null, supportedLanguages = [], theme = {}) => {
  // Use the new code block processor for consistency
  return processCodeBlocks(text, {
    onCodeBlock: ({ language, content: codeContent, isComplete, key, theme: blockTheme }) => {
      // Use CodeBlockWithExecution if code execution is enabled and language is executable
      if (enableCodeExecution && isLanguageExecutable && isLanguageExecutable(language)) {
        return (
          <CodeBlockWithExecution
            key={key}
            language={language}
            content={codeContent}
            theme={blockTheme || theme}
            supportedLanguages={supportedLanguages}
            onExecutionComplete={(result, error, executionTime) => {
              console.log('Code execution completed:', { result, error, executionTime });
            }}
          />
        );
      }

      // Fall back to regular code block for non-executable languages
      return (
        <CodeBlock key={key} theme={blockTheme || theme}>
          <CodeHeader theme={blockTheme || theme}>
            <CodeLanguage theme={blockTheme || theme}>{language}</CodeLanguage>
            <CopyButton theme={blockTheme || theme} onClick={() => navigator.clipboard.writeText(codeContent)}>
              Copy
            </CopyButton>
          </CodeHeader>
          <Pre theme={blockTheme || theme}>{codeContent}</Pre>
        </CodeBlock>
      );
    },
    onTextSegment: (textSegment) => processMarkdown(textSegment, theme),
    theme
  });
};

// Update processMarkdown to handle LaTeX
const processMarkdown = (text, theme = {}) => {
  const parts = [];
  let lastIndex = 0;
  let keyCounter = 0;

  // Regex for display math: $$\n?...$$\n? or $$...$$
  const displayRegex = /\$\$\s*([\s\S]*?)\s*\$\$/g;
  // Regex for inline math: $...$ (not starting/ending with space)
  const inlineRegex = /\$([^\s].*?[^\s])\$/g;

  const pushInlineSegments = (segment) => {
    let inlineLastIndex = 0;
    let inlineMatch;

    while ((inlineMatch = inlineRegex.exec(segment)) !== null) {
      if (inlineMatch.index > inlineLastIndex) {
        parts.push(
          <span key={`text-${keyCounter++}`}>
            {processMarkdownText(segment.substring(inlineLastIndex, inlineMatch.index), theme)}
          </span>
        );
      }

      parts.push(renderLatex(inlineMatch[1], false, `inline-${keyCounter++}`));
      inlineLastIndex = inlineMatch.index + inlineMatch[0].length;
    }

    if (inlineLastIndex < segment.length) {
      parts.push(
        <span key={`text-${keyCounter++}`}>
          {processMarkdownText(segment.substring(inlineLastIndex), theme)}
        </span>
      );
    }

    inlineRegex.lastIndex = 0;
  };

  let match;
  while ((match = displayRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      pushInlineSegments(text.substring(lastIndex, match.index));
    }

    parts.push(renderLatex(match[1], true, `display-${keyCounter++}`));
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    pushInlineSegments(text.substring(lastIndex));
  }

  return <>{parts}</>;
};

// New function for processing non-LaTeX markdown text (lines, bullets, etc.)
const processMarkdownText = (text, theme = {}) => {
  const lines = text.split('\n');
  const result = [];
  let inList = false;
  let inNumberedList = false;
  let listItems = [];
  let numberedListItems = [];

  // Process line by line
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Headings
    if (line.startsWith('# ')) {
      if (inList) {
        result.push(
          <BulletList key={`list-${i}`} theme={theme}>
            {listItems}
          </BulletList>
        );
        inList = false;
        listItems = [];
      }
      if (inNumberedList) {
        result.push(
          <NumberedList key={`nlist-${i}`} theme={theme}>
            {numberedListItems}
          </NumberedList>
        );
        inNumberedList = false;
        numberedListItems = [];
      }
      result.push(
        <Heading1 key={`h1-${i}`} theme={theme}>
          {processInlineFormatting(line.substring(2), theme)}
        </Heading1>
      );
      continue;
    }

    if (line.startsWith('## ')) {
      if (inList) {
        result.push(
          <BulletList key={`list-${i}`} theme={theme}>
            {listItems}
          </BulletList>
        );
        inList = false;
        listItems = [];
      }
      if (inNumberedList) {
        result.push(
          <NumberedList key={`nlist-${i}`} theme={theme}>
            {numberedListItems}
          </NumberedList>
        );
        inNumberedList = false;
        numberedListItems = [];
      }
      result.push(
        <Heading2 key={`h2-${i}`} theme={theme}>
          {processInlineFormatting(line.substring(3), theme)}
        </Heading2>
      );
      continue;
    }

    if (line.startsWith('### ')) {
      if (inList) {
        result.push(
          <BulletList key={`list-${i}`} theme={theme}>
            {listItems}
          </BulletList>
        );
        inList = false;
        listItems = [];
      }
      if (inNumberedList) {
        result.push(
          <NumberedList key={`nlist-${i}`} theme={theme}>
            {numberedListItems}
          </NumberedList>
        );
        inNumberedList = false;
        numberedListItems = [];
      }
      result.push(
        <Heading3 key={`h3-${i}`} theme={theme}>
          {processInlineFormatting(line.substring(4), theme)}
        </Heading3>
      );
      continue;
    }

    if (line.startsWith('#### ')) {
      if (inList) {
        result.push(
          <BulletList key={`list-${i}`} theme={theme}>
            {listItems}
          </BulletList>
        );
        inList = false;
        listItems = [];
      }
      if (inNumberedList) {
        result.push(
          <NumberedList key={`nlist-${i}`} theme={theme}>
            {numberedListItems}
          </NumberedList>
        );
        inNumberedList = false;
        numberedListItems = [];
      }
      result.push(
        <Heading4 key={`h4-${i}`} theme={theme}>
          {processInlineFormatting(line.substring(5), theme)}
        </Heading4>
      );
      continue;
    }

    if (line.startsWith('##### ')) {
      if (inList) {
        result.push(
          <BulletList key={`list-${i}`} theme={theme}>
            {listItems}
          </BulletList>
        );
        inList = false;
        listItems = [];
      }
      if (inNumberedList) {
        result.push(
          <NumberedList key={`nlist-${i}`} theme={theme}>
            {numberedListItems}
          </NumberedList>
        );
        inNumberedList = false;
        numberedListItems = [];
      }
      result.push(
        <Heading5 key={`h5-${i}`} theme={theme}>
          {processInlineFormatting(line.substring(6), theme)}
        </Heading5>
      );
      continue;
    }

    if (line.startsWith('###### ')) {
      if (inList) {
        result.push(
          <BulletList key={`list-${i}`} theme={theme}>
            {listItems}
          </BulletList>
        );
        inList = false;
        listItems = [];
      }
      if (inNumberedList) {
        result.push(
          <NumberedList key={`nlist-${i}`} theme={theme}>
            {numberedListItems}
          </NumberedList>
        );
        inNumberedList = false;
        numberedListItems = [];
      }
      result.push(
        <Heading6 key={`h6-${i}`} theme={theme}>
          {processInlineFormatting(line.substring(7), theme)}
        </Heading6>
      );
      continue;
    }

    // Horizontal rule
    if (line === '---' || line === '***' || line === '___') {
      if (inList) {
        result.push(
          <BulletList key={`list-${i}`} theme={theme}>
            {listItems}
          </BulletList>
        );
        inList = false;
        listItems = [];
      }
      if (inNumberedList) {
        result.push(
          <NumberedList key={`nlist-${i}`} theme={theme}>
            {numberedListItems}
          </NumberedList>
        );
        inNumberedList = false;
        numberedListItems = [];
      }
      result.push(<HorizontalRule key={`hr-${i}`} theme={theme} />);
      continue;
    }

    // Blockquote
    if (line.startsWith('> ')) {
      if (inList) {
        result.push(
          <BulletList key={`list-${i}`}>
            {listItems}
          </BulletList>
        );
        inList = false;
        listItems = [];
      }
      if (inNumberedList) {
        result.push(
          <NumberedList key={`nlist-${i}`} theme={theme}>
            {numberedListItems}
          </NumberedList>
        );
        inNumberedList = false;
        numberedListItems = [];
      }
      result.push(
        <Blockquote key={`quote-${i}`} theme={theme}>
          <Paragraph theme={theme}>
            {processInlineFormatting(line.substring(2), theme)}
          </Paragraph>
        </Blockquote>
      );
      continue;
    }

    // Bullet point
    if (line.startsWith('* ') || line.startsWith('- ')) {
      if (inNumberedList) {
        result.push(
          <NumberedList key={`nlist-${i}`} theme={theme}>
            {numberedListItems}
          </NumberedList>
        );
        inNumberedList = false;
        numberedListItems = [];
      }
      inList = true;
      const itemContent = line.substring(2);
      listItems.push(
        <li key={`item-${i}`}>{processInlineFormatting(itemContent, theme)}</li>
      );
      continue;
    }

    // Numbered list
    const numberedMatch = line.match(/^(\d+)\.\s/);
    if (numberedMatch) {
      if (inList) {
        result.push(
          <BulletList key={`list-${i}`} theme={theme}>
            {listItems}
          </BulletList>
        );
        inList = false;
        listItems = [];
      }
      inNumberedList = true;
      const itemContent = line.substring(numberedMatch[0].length);
      numberedListItems.push(
        <li key={`nitem-${i}`}>{processInlineFormatting(itemContent, theme)}</li>
      );
      continue;
    }

    // End of lists
    if ((inList || inNumberedList) && line === '') {
      if (inList) {
        result.push(
          <BulletList key={`list-${i}`} theme={theme}>
            {listItems}
          </BulletList>
        );
        inList = false;
        listItems = [];
      }
      if (inNumberedList) {
        result.push(
          <NumberedList key={`nlist-${i}`} theme={theme}>
            {numberedListItems}
          </NumberedList>
        );
        inNumberedList = false;
        numberedListItems = [];
      }
      continue;
    }

    // Regular text line
    if (!inList && !inNumberedList && line !== '') {
      result.push(
        <Paragraph key={`p-${i}`} theme={theme}>
          {processInlineFormatting(line, theme)}
        </Paragraph>
      );
    } else if (!inList && !inNumberedList) {
      // Empty line
      result.push(<br key={`br-${i}`} />);
    }
  }

  // Add any remaining list items
  if (inList && listItems.length > 0) {
    result.push(
      <BulletList key="list-end" theme={theme}>
        {listItems}
      </BulletList>
    );
  }

  if (inNumberedList && numberedListItems.length > 0) {
    result.push(
      <NumberedList key="nlist-end" theme={theme}>
        {numberedListItems}
      </NumberedList>
    );
  }

  return <>{result}</>;
};

// Process inline formatting (bold, italic, links)
const processInlineFormatting = (text, theme = {}) => {
  const parts = [];
  let lastIndex = 0;
  let keyCounter = 0;

  // Handle links first
  const linkPattern = /\[([^\]]+)\]\(([^)]+)\)/g;
  let match;

  while ((match = linkPattern.exec(text)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      const beforeText = text.substring(lastIndex, match.index);
      parts.push(<span key={`text-${keyCounter++}`}>{processBoldItalic(beforeText, theme)}</span>);
    }

    // Add the link
    parts.push(
      <Link key={`link-${keyCounter++}`} href={match[2]} target="_blank" rel="noopener noreferrer" theme={theme}>
        {processBoldItalic(match[1], theme)}
      </Link>
    );

    lastIndex = match.index + match[0].length;
  }

  // Add any remaining text
  if (lastIndex < text.length) {
    const remainingText = text.substring(lastIndex);
    parts.push(<span key={`text-${keyCounter++}`}>{processBoldItalic(remainingText, theme)}</span>);
  }

  return parts.length > 0 ? <>{parts}</> : processBoldItalic(text, theme);
};

// Process bold and italic formatting
const processBoldItalic = (text, theme = {}) => {
  // First handle bold text
  const boldPattern = /\*\*(.*?)\*\*/g;
  const parts = [];
  let lastIndex = 0;
  let keyCounter = 0;
  let match;

  while ((match = boldPattern.exec(text)) !== null) {
    // Add text before the bold part
    if (match.index > lastIndex) {
      parts.push(<span key={`text-${keyCounter++}`}>{processItalic(text.substring(lastIndex, match.index), theme)}</span>);
    }

    // Add the bold text (also process any italic within it)
    parts.push(<Bold key={`bold-${keyCounter++}`} theme={theme}>{processItalic(match[1], theme)}</Bold>);

    lastIndex = match.index + match[0].length;
  }

  // Add any remaining text
  if (lastIndex < text.length) {
    parts.push(<span key={`text-${keyCounter++}`}>{processItalic(text.substring(lastIndex), theme)}</span>);
  }

  return parts.length > 0 ? <>{parts}</> : processItalic(text, theme);
};

// Process italic text
const processItalic = (text, theme = {}) => {
  if (!text) return null;

  const italicPattern = /\*((?!\*).+?)\*/g;
  const parts = [];
  let lastIndex = 0;
  let keyCounter = 0;
  let match;

  while ((match = italicPattern.exec(text)) !== null) {
    // Add text before the italic part
    if (match.index > lastIndex) {
      parts.push(<span key={`text-${keyCounter++}`}>{text.substring(lastIndex, match.index)}</span>);
    }

    // Add the italic text
    parts.push(<Italic key={`italic-${keyCounter++}`} theme={theme}>{match[1]}</Italic>);

    lastIndex = match.index + match[0].length;
  }

  // Add any remaining text
  if (lastIndex < text.length) {
    parts.push(<span key={`text-${keyCounter++}`}>{text.substring(lastIndex)}</span>);
  }

  return <>{parts.length > 0 ? parts : text}</>;
};

const CodeBlock = styled.div`
  background: ${props => props.theme.codeBlockBg || (props.theme.isDark ? 'rgba(24, 24, 27, 0.95)' : '#f8f9fb')};
  border-radius: 10px;
  margin: 14px 0;
  overflow: hidden;
  border: 1px solid ${props => props.theme.codeBlockBorder || (props.theme.isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)')};
  max-width: 100%;
  width: 100%;
`;

const CodeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 14px;
  background: ${props => props.theme.codeBlockHeaderBg || (props.theme.isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)')};
  border-bottom: 1px solid ${props => props.theme.codeBlockBorder || (props.theme.isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)')};
`;

const CodeLanguage = styled.div`
  font-size: 0.78rem;
  font-weight: 500;
  color: ${props => `${props.theme.text}88`};
  letter-spacing: 0.01em;
  text-transform: lowercase;
`;

const CopyButton = styled.button`
  background: none;
  border: none;
  color: ${props => `${props.theme.text}66`};
  font-size: 0.78rem;
  cursor: pointer;
  padding: 2px 6px;
  font-weight: 500;
  border-radius: 4px;
  transition: all 0.15s ease;
  
  &:hover {
    color: ${props => props.theme.text};
    background: ${props => `${props.theme.text}0a`};
  }
`;

const Pre = styled.pre`
  margin: 0;
  padding: 14px 16px;
  overflow-x: auto;
  font-family: 'SF Mono', SFMono-Regular, ui-monospace, Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 0.84rem;
  line-height: 1.6;
  max-width: 100%;
  word-wrap: normal;
  white-space: pre;
  text-overflow: ellipsis;
  color: ${props => props.theme.text};
  
  &::-webkit-scrollbar {
    height: 5px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => `${props.theme.text}18`};
    border-radius: 3px;
  }
`;

const Message = styled.div`
  display: flex;
  flex-direction: ${props => props.$alignment === 'right' ? 'row-reverse' : 'row'};
  align-items: flex-start;
  justify-content: flex-start;
  margin-bottom: var(--message-spacing, 28px);
  max-width: 780px;
  width: 100%;
  padding-left: ${props => props.$alignment === 'left' ? '16px' : '16px'};
  padding-right: ${props => props.$alignment === 'right' ? '16px' : '16px'};
`;

const Avatar = styled.div`
  width: ${props => props.role === 'user' ? '26px' : '28px'};
  height: ${props => props.role === 'user' ? '26px' : '28px'};
  border-radius: ${props => props.role === 'user' ? '50%' : '8px'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${props => props.$alignment === 'right' ? '0' : '12px'};
  margin-left: ${props => props.$alignment === 'left' ? '0' : '12px'};
  margin-top: 2px;
  font-weight: 600;
  flex-shrink: 0;
  background: ${props => props.$profilePicture
    ? `url(${props.$profilePicture}) center/cover`
    : (props.$useModelIcon
      ? 'transparent'
      : (props.role === 'user'
        ? `${props.theme.text}12`
        : props.theme.secondary))};
  color: ${props => props.$profilePicture ? 'transparent' : (props.role === 'user' ? `${props.theme.text}70` : 'white')};
  transition: all 0.2s ease;
  box-shadow: none;
  opacity: 1;
  
  &:hover {
    opacity: 0.85;
  }
  
  svg {
    width: ${props => props.role === 'user' ? '14px' : '18px'};
    height: ${props => props.role === 'user' ? '14px' : '18px'};
  }
`;

const MessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: ${props => props.role === 'user' ? '72%' : 'calc(100% - 52px)'};
  flex: ${props => props.role === 'user' ? '0 1 auto' : '1'};
  align-items: ${props => props.$alignment === 'right' ? 'flex-end' : 'flex-start'};
  
  @media (max-width: 768px) {
    max-width: ${props => props.role === 'user' ? '85%' : 'calc(100% - 52px)'};
  }
`;

const isCustomAccent = (theme) => theme?.accentChoice && theme.accentChoice !== 'theme';

const getBubbleBackground = (role, theme) => {
  const customAccent = isCustomAccent(theme);
  if (role === 'user') {
    if (customAccent) return theme.accentBackground;
    return theme.isDark ? `${theme.text}0c` : `${theme.text}08`;
  }
  return 'transparent';
};

const getBubbleBorderColor = (role, theme) => {
  if (role !== 'user') return 'transparent';
  return isCustomAccent(theme) ? theme.accentColor : theme.border;
};

const getBubbleTextColor = (theme) => isCustomAccent(theme) ? '#FFFFFF' : theme.text;

const Content = styled.div`
  width: ${props => props.role === 'user' ? 'fit-content' : '100%'};
  white-space: ${props => props.role === 'user' ? 'pre-wrap' : 'normal'};
  color: ${props => getBubbleTextColor(props.theme)};
  line-height: var(--line-height, 1.6);
  overflow: hidden;
  flex: 1;
  margin-left: ${props => props.$alignment === 'right' ? 'auto' : '0'};
  margin-right: ${props => props.$alignment === 'left' ? 'auto' : '0'};
  position: relative;
  text-align: ${props => props.$alignment === 'right' ? 'right' : 'left'};
  direction: ltr;
  unicode-bidi: plaintext;
  transition: background 0.2s ease, border-color 0.2s ease;
  background: ${props => getBubbleBackground(props.role, props.theme)};
  border: ${props => props.role === 'user'
    ? `var(--bubble-border, 1px solid ${getBubbleBorderColor(props.role, props.theme)})`
    : 'none'};
  border-radius: ${props => props.role === 'user'
    ? 'var(--bubble-radius, 20px)'
    : '0'};
  padding: ${props => props.role === 'user'
    ? 'var(--bubble-padding-vertical, 12px) var(--bubble-padding-horizontal, 18px)'
    : '2px 0'};
  box-shadow: none;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  font-size: 0.94rem;
  letter-spacing: -0.008em;
  
  ${props => props.theme.name === 'bisexual' && props.role === 'user' && `
    border: none !important;
    &::before {
      content: '';
      position: absolute;
      top: -1px;
      left: -1px;
      right: -1px;
      bottom: -1px;
      background: linear-gradient(145deg, #D60270, #9B4F96);
      border-radius: 21px;
      z-index: -1;
      opacity: 0.3;
    }
  `}
  
  & > ${CodeBlock} {
    max-width: 100%;
    text-align: left;
    direction: ltr;
  }
  
  & > em:last-child {
    display: block;
    margin-top: 12px;
    opacity: 0.5;
    font-size: 0.82em;
    text-align: right;
    font-style: normal;
    color: ${props => props.theme.text}99;
    ${props => props.theme.name === 'bisexual' && `
      background: ${props.theme.accentGradient};
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      opacity: 0.9;
    `}
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const Timestamp = styled.div`
  font-size: 0.72rem;
  color: ${props => `${props.theme.text}55`};
  display: flex;
  align-items: center;
  letter-spacing: 0.01em;
`;

const MessageActions = styled.div`
  display: flex;
  justify-content: ${props => props.$alignment === 'right' ? 'flex-end' : 'flex-start'};
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  padding: 0;
  opacity: 0.7;
  width: ${props => props.$alignment === 'right' ? '100%' : 'fit-content'};
  max-width: 100%;
  align-self: ${props => props.$alignment === 'right' ? 'flex-end' : 'flex-start'};
  transition: opacity 0.15s ease;
  
  &:hover {
    opacity: 1;
  }
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  font-size: 0.78rem;
  color: ${props => `${props.theme.text}50`};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.15s ease;
  
  &:hover {
    background: ${props => `${props.theme.text}08`};
    color: ${props => `${props.theme.text}90`};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    width: 15px;
    height: 15px;
  }
`;

/* ---------------- Deep Research in-chat UI ---------------- */

const drOrbit = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;
const drOrbitReverse = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(-360deg); }
`;
const drPulseCore = keyframes`
  0%, 100% { transform: scale(1);   box-shadow: 0 0 0 0 rgba(37,99,235,0.45); }
  50%      { transform: scale(1.08); box-shadow: 0 0 0 10px rgba(37,99,235,0); }
`;
const drShimmerMove = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;
const drFadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const DRCard = styled.div`
  border: 1px solid ${props => props.theme.border || 'rgba(148,163,184,0.25)'};
  background: ${props => props.theme.inputBackground || 'rgba(148,163,184,0.04)'};
  border-radius: 14px;
  padding: 16px 18px;
  margin-top: 4px;
  animation: ${drFadeIn} 260ms ease;
`;

const DRLoadingBody = styled.div`
  display: flex;
  gap: 18px;
  align-items: center;

  @media (max-width: 520px) {
    flex-direction: column;
    align-items: stretch;
    gap: 14px;
  }
`;

const DROrbit = styled.div`
  position: relative;
  width: 78px;
  height: 78px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  .ring {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 1px dashed rgba(37,99,235,0.22);
  }
  .ring.inner { inset: 14px; border-style: solid; border-color: rgba(37,99,235,0.14); }

  .track {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    animation: ${drOrbit} 4.8s linear infinite;
  }
  .track.reverse {
    animation: ${drOrbitReverse} 6.4s linear infinite;
    inset: 10px;
  }
  .track.slow {
    animation: ${drOrbit} 9s linear infinite;
    inset: 18px;
  }

  .orb {
    position: absolute;
    top: -4px;
    left: 50%;
    transform: translateX(-50%);
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #60a5fa;
    box-shadow: 0 0 10px rgba(96,165,250,0.75);
  }
  .track.reverse .orb { background: #2563eb; box-shadow: 0 0 10px rgba(37,99,235,0.75); }
  .track.slow    .orb { background: #93c5fd; box-shadow: 0 0 10px rgba(147,197,253,0.6); width: 6px; height: 6px; }

  .core {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    animation: ${drPulseCore} 2.2s ease-in-out infinite;
    position: relative;
    z-index: 1;
    box-shadow: 0 6px 16px rgba(37,99,235,0.35);
  }
  .core svg { width: 14px; height: 14px; }
`;

const DRLoadingText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;

  .title {
    font-size: 0.94rem;
    font-weight: 600;
    color: ${props => props.theme.text};
    letter-spacing: -0.005em;
  }
  .step {
    font-size: 0.82rem;
    color: ${props => `${props.theme.text}90`};
    opacity: 0.85;
  }
  .query {
    font-size: 0.78rem;
    color: ${props => `${props.theme.text}70`};
    font-style: italic;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin-top: 2px;
  }
`;

const DRShimmerBar = styled.div`
  position: relative;
  width: 100%;
  height: 6px;
  border-radius: 999px;
  background: ${props => `${props.theme.text}10`};
  overflow: hidden;
  margin-top: 4px;

  .fill {
    height: 100%;
    border-radius: 999px;
    width: ${props => Math.max(4, Math.min(100, props.$progress || 0))}%;
    background: linear-gradient(
      90deg,
      rgba(29,78,216,0.9) 0%,
      rgba(96,165,250,1) 45%,
      rgba(147,197,253,1) 55%,
      rgba(29,78,216,0.9) 100%
    );
    background-size: 200% 100%;
    animation: ${drShimmerMove} 2.2s linear infinite;
    transition: width 0.4s ease;
  }
`;

const DRResultBadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  flex-wrap: wrap;
`;

const DRBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px 4px 8px;
  border-radius: 999px;
  background: linear-gradient(135deg, rgba(37,99,235,0.12) 0%, rgba(96,165,250,0.08) 100%);
  border: 1px solid rgba(37,99,235,0.18);
  color: ${props => props.theme.text};
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.01em;

  svg { width: 12px; height: 12px; color: #2563eb; }
`;

const DRMetaInline = styled.span`
  font-size: 0.78rem;
  color: ${props => `${props.theme.text}88`};
  opacity: 0.85;
  min-width: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DRDetailsToggle = styled.button`
  background: none;
  border: 1px solid ${props => props.theme.border || 'rgba(148,163,184,0.3)'};
  padding: 4px 10px;
  border-radius: 999px;
  color: ${props => `${props.theme.text}aa`};
  font-size: 0.74rem;
  cursor: pointer;
  transition: all 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 500;

  &:hover {
    border-color: rgba(37,99,235,0.4);
    color: ${props => props.theme.text};
    background: rgba(37,99,235,0.06);
  }

  svg {
    width: 11px;
    height: 11px;
    transition: transform 0.2s ease;
    transform: rotate(${props => props.$open ? '180deg' : '0deg'});
  }
`;

const DRDetailsPanel = styled.div`
  margin-bottom: 14px;
  padding: 12px 14px;
  border-radius: 10px;
  background: ${props => `${props.theme.text}06`};
  border: 1px solid ${props => props.theme.border || 'rgba(148,163,184,0.18)'};
  font-size: 0.82rem;
  animation: ${drFadeIn} 180ms ease;

  .dr-details-section {
    & + .dr-details-section { margin-top: 10px; }
  }
  .dr-details-label {
    font-size: 0.66rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${props => `${props.theme.text}88`};
    margin-bottom: 4px;
    font-weight: 600;
  }
  .dr-details-row {
    display: grid;
    grid-template-columns: repeat(3, minmax(0,1fr));
    gap: 6px 14px;
    color: ${props => props.theme.text};
    opacity: 0.9;
    font-size: 0.78rem;
    @media (max-width: 560px) { grid-template-columns: repeat(2, minmax(0,1fr)); }
    .dr-kv {
      display: flex; flex-direction: column; gap: 2px; min-width: 0;
      .k { font-size: 0.66rem; letter-spacing: 0.06em; text-transform: uppercase; color: ${props => `${props.theme.text}88`}; }
      .v { font-size: 0.82rem; word-break: break-word; }
    }
  }
  ul.dr-sub-list {
    margin: 0; padding-left: 18px; line-height: 1.5; color: ${props => props.theme.text}; opacity: 0.9;
    li { margin-bottom: 3px; }
  }
  .dr-quality {
    background: rgba(239,68,68,0.08);
    border: 1px solid rgba(239,68,68,0.25);
    color: #b91c1c;
    border-radius: 8px;
    padding: 8px 10px;
    font-size: 0.78rem;
    line-height: 1.45;
  }
`;

const DRBody = styled.div`
  font-size: 0.95rem;
  line-height: 1.68;
  color: ${props => props.theme.text};

  /* Headings from robustFormatContent keep their own sizing, just tighten spacing */
  h1, h2, h3, h4, h5, h6 { margin-top: 1em; margin-bottom: 0.35em; }
  p { margin: 0 0 0.7em; }
  ul, ol { margin: 0.2em 0 0.8em; padding-left: 22px; }
  li { margin-bottom: 4px; }
  blockquote { border-left: 3px solid rgba(37,99,235,0.4); }
`;

const DRActionBar = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid ${props => `${props.theme.text}12`};
  flex-wrap: wrap;
`;

const DRSpinIcon = styled.svg`
  animation: ${drOrbit} 1s linear infinite;
`;

const DRActionPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid ${props => props.theme.border || 'rgba(148,163,184,0.3)'};
  background: ${props => props.$primary ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' : 'transparent'};
  color: ${props => props.$primary ? '#fff' : props.theme.text};
  font-size: 0.78rem;
  font-weight: ${props => props.$primary ? '600' : '500'};
  cursor: pointer;
  transition: all 0.15s ease;
  ${props => props.$primary && `box-shadow: 0 2px 6px rgba(37,99,235,0.25); border-color: transparent;`}

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    ${props => props.$primary
      ? `box-shadow: 0 4px 12px rgba(37,99,235,0.35);`
      : `background: rgba(37,99,235,0.08); border-color: rgba(37,99,235,0.4);`
    }
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
  }

  svg { width: 14px; height: 14px; }
`;

const ErrorMessage = styled(Content)`
  background: rgba(239, 68, 68, 0.06);
  border: 1px solid rgba(239, 68, 68, 0.12);
  border-radius: 12px;
  padding: 12px 16px;
`;

const pulse = keyframes`
  0% { opacity: 0.5; }
  50% { opacity: 1; }
  100% { opacity: 0.5; }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const ThinkingContainer = styled.div`
  display: flex;
  align-items: center;
  opacity: 0.7;
  font-style: italic;
`;

const SpinnerIcon = styled.div`
  width: 14px;
  height: 14px;
  border: 1.75px solid ${props => `${props.theme.text}26`};
  border-top-color: ${props => props.theme.text};
  border-radius: 50%;
  margin-right: 8px;
  flex-shrink: 0;
  animation: ${spin} 0.85s linear infinite;
  box-sizing: border-box;
`;

const LoadingDots = styled.span`
  display: inline-block;
  animation: ${pulse} 1.5s infinite;
`;

// Add style components for markdown formatting aligned with design language
const Bold = styled.span`
  font-weight: 700;
  color: ${props => props.theme.text};
`;

const Italic = styled.span`
  font-style: italic;
  color: ${props => props.theme.text};
`;

const Heading1 = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0.8em 0 0.3em 0;
  color: ${props => props.theme.text};
  border-bottom: 1px solid ${props => props.theme.border};
  padding-bottom: 0.25rem;
  line-height: 1.3;
  letter-spacing: -0.02em;
  
  &:first-child {
    margin-top: 0;
  }
`;

const Heading2 = styled.h2`
  font-size: 1.25rem;
  font-weight: 650;
  margin: 0.7em 0 0.25em 0;
  color: ${props => props.theme.text};
  line-height: 1.3;
  letter-spacing: -0.015em;
  
  &:first-child {
    margin-top: 0;
  }
`;

const Heading3 = styled.h3`
  font-size: 1.08rem;
  font-weight: 600;
  margin: 0.6em 0 0.2em 0;
  color: ${props => props.theme.text};
  line-height: 1.3;
  letter-spacing: -0.01em;
  
  &:first-child {
    margin-top: 0;
  }
`;

const Heading4 = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  margin: 0.5em 0 0.2em 0;
  color: ${props => props.theme.text};
  line-height: 1.3;
  
  &:first-child {
    margin-top: 0;
  }
`;

const Heading5 = styled.h5`
  font-size: 0.92rem;
  font-weight: 600;
  margin: 0.4em 0 0.15em 0;
  color: ${props => props.theme.text};
  line-height: 1.3;
  
  &:first-child {
    margin-top: 0;
  }
`;

const Heading6 = styled.h6`
  font-size: 0.85rem;
  font-weight: 600;
  margin: 0.35em 0 0.1em 0;
  color: ${props => props.theme.text};
  line-height: 1.3;
  
  &:first-child {
    margin-top: 0;
  }
`;

const Paragraph = styled.p`
  margin: 0.4em 0;
  line-height: 1.6;
  color: ${props => props.theme.text};
  
  &:first-child {
    margin-top: 0;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const BulletList = styled.ul`
  list-style-type: none;
  padding-left: 1em;
  margin: 0.35em 0;
  
  li {
    position: relative;
    padding-left: 1.3em;
    margin: 0.2em 0;
    line-height: 1.6;
    color: ${props => props.theme.text};
    
    &:before {
      content: "•";
      position: absolute;
      left: 0.15em;
      color: ${props => props.theme.accentColor || props.theme.text};
      font-weight: bold;
      font-size: 1em;
      opacity: 0.55;
    }
  }
`;

const NumberedList = styled.ol`
  padding-left: 1.4em;
  margin: 0.35em 0;
  
  li {
    margin: 0.2em 0;
    line-height: 1.6;
    color: ${props => props.theme.text};
    padding-left: 0.2em;
    
    &::marker {
      color: ${props => props.theme.accentColor || props.theme.text};
      opacity: 0.55;
      font-weight: 600;
      font-size: 0.9em;
    }
  }
`;

const Blockquote = styled.blockquote`
  border-left: 3px solid ${props => `${props.theme.accentColor || props.theme.text}44`};
  margin: 0.4em 0;
  padding: 0.3rem 0 0.3rem 0.9rem;
  background: transparent;
  border-radius: 0;
  font-style: italic;
  color: ${props => `${props.theme.text}cc`};
  
  p {
    margin: 0;
    line-height: 1.6;
  }
`;

const Link = styled.a`
  color: ${props => props.theme.accentColor || props.theme.primary};
  text-decoration: underline;
  text-decoration-color: ${props => `${props.theme.accentColor || props.theme.primary}44`};
  text-underline-offset: 2px;
  transition: text-decoration-color 0.15s ease;
  
  &:hover {
    text-decoration-color: ${props => props.theme.accentColor || props.theme.primary};
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  overflow: hidden;
  background: transparent;
  font-size: 0.88rem;
`;

const TableHeader = styled.th`
  background: ${props => `${props.theme.text}06`};
  padding: 10px 14px;
  text-align: left;
  font-weight: 600;
  font-size: 0.82rem;
  color: ${props => `${props.theme.text}cc`};
  border-bottom: 1px solid ${props => props.theme.border};
  letter-spacing: 0.01em;
`;

const TableCell = styled.td`
  padding: 10px 14px;
  border-bottom: 1px solid ${props => props.theme.border};
  color: ${props => props.theme.text};
  
  &:last-child {
    border-bottom: none;
  }
`;

const TableRow = styled.tr`
  &:last-child td {
    border-bottom: none;
  }
  
  &:hover {
    background: ${props => `${props.theme.text}04`};
  }
`;

const HorizontalRule = styled.hr`
  border: none;
  height: 1px;
  background: ${props => props.theme.border};
  margin: 1rem 0;
  border-radius: 1px;
`;

const MessageImage = styled.img`
  max-width: 100%;
  max-height: 300px;
  border-radius: 12px;
  margin-bottom: 12px;
  object-fit: contain;
  background: ${props => props.theme.codeBlockBg || (props.theme.isDark ? 'rgba(30, 30, 30, 0.8)' : 'rgba(246, 248, 250, 0.8)')};
`;

const MessageVideo = styled.video`
  max-width: 100%;
  max-height: 400px;
  border-radius: 12px;
  margin-bottom: 12px;
  object-fit: contain;
  background: ${props => props.theme.codeBlockBg || (props.theme.isDark ? 'rgba(30, 30, 30, 0.8)' : 'rgba(246, 248, 250, 0.8)')};
`;

// Flowchart components
const FlowchartContainer = styled.div`
  margin: 12px 0;
  padding: 16px;
  background: ${props => props.theme.codeBlockBg || (props.theme.isDark ? 'rgba(30, 30, 30, 0.8)' : 'rgba(246, 248, 250, 0.8)')};
  border-radius: 12px;
  border: 1px solid ${props => props.theme.border};
`;

const FlowchartButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 8px;
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
  
  svg {
    flex-shrink: 0;
  }
`;

const FlowchartPreview = styled.div`
  margin-top: 8px;
  padding: 8px;
  background: ${props => props.theme.isDark ? 'rgba(45, 45, 45, 0.5)' : 'rgba(255, 255, 255, 0.5)'};
  border-radius: 6px;
  border: 1px dashed ${props => props.theme.border};
`;

// New component for PDF file attachment indicator
const FileAttachmentContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  background: ${props => props.theme.codeBlockBg || (props.theme.isDark ? 'rgba(30, 30, 30, 0.8)' : 'rgba(246, 248, 250, 0.8)')};
  border: 1px solid ${props => props.theme.border};
  max-width: fit-content;
`;

const FileIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  color: #e64a3b; /* PDF red color */
`;

const FileName = styled.span`
  font-size: 0.9rem;
  font-weight: 500;
  color: ${props => props.theme.text};
  word-break: break-word;
`;

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';

  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// New styled components for sources display
const SourcesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const SourceButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 16px;
  border: 1px solid ${props => props.theme.border};
  background: ${props => props.theme.codeBlockBg || (props.theme.isDark ? 'rgba(30, 30, 30, 0.8)' : 'rgba(246, 248, 250, 0.8)')};
  color: ${props => props.theme.text};
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.codeBlockHeaderBg || (props.theme.isDark ? 'rgba(45, 45, 45, 0.9)' : 'rgba(240, 240, 240, 0.9)')};
    border-color: ${props => props.theme.primary.split(',')[0].replace('linear-gradient(145deg', '').trim()};
  }
`;

const SourceFavicon = styled.img`
  width: 16px;
  height: 16px;
  object-fit: contain;
  border-radius: 2px;
`;

// Add a ThinkingDropdown component
const ThinkingDropdownContainer = styled.div`
  margin: 1px 0 12px;
  overflow: hidden;
  width: 100%;
`;

const ThinkingHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 0 0 4px;
  cursor: pointer;
  user-select: none;
  font-weight: 500;
  font-size: 0.98rem;
  line-height: 1.35;
  color: ${props => `${props.theme.text}96`};
  transition: color 0.15s ease;

  &:hover {
    color: ${props => props.theme.text};
  }
`;

const HeaderSpinner = styled.span`
  width: 13px;
  height: 13px;
  border: 1.75px solid ${props => `${props.theme.text}26`};
  border-top-color: ${props => props.theme.text};
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
  box-sizing: border-box;
  animation: ${spin} 0.85s linear infinite;
`;

const ThinkingHeaderTitle = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1 1 auto;
`;

const ThinkingPreview = styled.div`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: inherit;
`;

const ThinkingTimerBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  margin-left: auto;
  padding: 0;
  color: ${props => `${props.theme.text}72`};
  font-variant-numeric: tabular-nums;
  font-size: 0.98rem;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1.35;
`;


const ThinkingArrow = styled.span`
  transition: transform 0.2s ease;
  transform: ${props => props.expanded ? 'rotate(90deg)' : 'rotate(0deg)'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  opacity: 0.65;
  flex-shrink: 0;

  svg {
    display: block;
  }
`;

const ThinkingContent = styled.div`
  padding: ${props => props.expanded ? '6px 0 2px 0' : '0'};
  max-height: ${props => props.expanded ? '1000px' : '0'};
  opacity: ${props => props.expanded ? '1' : '0'};
  transition: max-height 0.3s ease, opacity 0.25s ease, padding 0.25s ease;
  overflow: hidden;
  font-size: 0.88rem;
  color: ${props => `${props.theme.text}cc`};
`;

const ToolActivitySection = styled.div`
  margin-bottom: 15px;
`;

const ToolActivitySectionHeader = styled.div`
  font-size: 0.9em;
  font-weight: 600;
  color: ${props => props.theme.text}dd;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ToolActivityItemHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const ToolActivityItem = styled.div`
  background: ${props => props.theme.cardBackground || (props.theme.isDark ? 'rgba(32, 33, 36, 0.8)' : 'rgba(248, 249, 250, 0.8)')};
  border: 1px solid ${props => props.theme.border || '#e1e5e9'};
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 8px;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ToolActivityIcon = styled.span`
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
`;

const ToolActivityName = styled.span`
  font-weight: 500;
  color: ${props => props.theme.text};
  flex: 1;
`;

const pulseAnimation = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.7; }
  100% { opacity: 1; }
`;

const ToolActivityStatus = styled.span`
  font-size: 0.75em;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  
  ${props => {
    switch (props.status) {
      case 'pending':
        return `
          background: rgba(251, 191, 36, 0.2);
          color: #f59e0b;
          border: 1px solid rgba(251, 191, 36, 0.3);
        `;
      case 'executing':
        return `
          background: rgba(59, 130, 246, 0.2);
          color: #3b82f6;
          border: 1px solid rgba(59, 130, 246, 0.3);
          animation: ${pulseAnimation} 2s infinite;
        `;
      case 'completed':
        return `
          background: rgba(16, 185, 129, 0.2);
          color: #10b981;
          border: 1px solid rgba(16, 185, 129, 0.3);
        `;
      case 'error':
        return `
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
          border: 1px solid rgba(239, 68, 68, 0.3);
        `;
      default:
        return `
          background: rgba(107, 114, 128, 0.2);
          color: #6b7280;
          border: 1px solid rgba(107, 114, 128, 0.3);
        `;
    }
  }}
`;

const ToolActivityDetail = styled.div`
  margin-top: 8px;
`;

const ToolActivityLabel = styled.div`
  font-size: 0.8em;
  font-weight: 500;
  color: ${props => props.theme.text}aa;
  margin-bottom: 4px;
`;

const ToolActivityValue = styled.div`
  font-size: 0.8em;
  background: ${props => props.theme.isDark ? 'rgba(20, 21, 24, 0.7)' : 'rgba(255, 255, 255, 0.7)'};
  border: 1px solid ${props => props.theme.border}50;
  border-radius: 4px;
  padding: 6px 8px;
  font-family: 'SF Mono', SFMono-Regular, Consolas, 'Liberation Mono', Menlo, monospace;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 80px;
  overflow-y: auto;
  color: ${props => props.theme.text}dd;
  
  &::-webkit-scrollbar {
    width: 3px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.border};
    border-radius: 2px;
  }
`;

const ToolActivityError = styled.div`
  font-size: 0.8em;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 4px;
  padding: 6px 8px;
  color: #ef4444;
  font-family: 'SF Mono', SFMono-Regular, Consolas, 'Liberation Mono', Menlo, monospace;
  white-space: pre-wrap;
  word-break: break-word;
`;

// Code Execution Components (for Gemini's code execution feature)
const CodeExecutionSection = styled.div`
  margin: 12px 0;
  border: 1px solid ${props => props.theme.border || '#e1e5e9'}40;
  border-radius: 8px;
  overflow: hidden;
  background: ${props => props.theme.background || '#f8f9fa'}20;
`;

const CodeExecutionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: ${props => props.theme.background || '#f8f9fa'}40;
  border-bottom: 1px solid ${props => props.theme.border || '#e1e5e9'}30;
  font-size: 0.85em;
  font-weight: 600;
  color: ${props => props.theme.text}dd;
`;

const CodeExecutionIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 4px;
  color: white;
  font-size: 10px;
`;

const CodeExecutionBody = styled.div`
  padding: 12px;
`;

const CodeExecutionCode = styled.pre`
  background: ${props => props.theme.codeBackground || '#1e1e1e'};
  color: ${props => props.theme.codeText || '#d4d4d4'};
  padding: 12px;
  border-radius: 6px;
  font-family: 'SF Mono', SFMono-Regular, Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 0.85em;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  max-height: 300px;
  
  &::-webkit-scrollbar {
    height: 6px;
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.border}60;
    border-radius: 3px;
  }
`;

const CodeExecutionResultSection = styled.div`
  margin-top: 12px;
`;

const CodeExecutionResultHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8em;
  font-weight: 600;
  color: ${props => props.outcome === 'OUTCOME_OK' ? '#10b981' : '#ef4444'};
  margin-bottom: 8px;
`;

const CodeExecutionOutput = styled.pre`
  background: ${props => props.outcome === 'OUTCOME_OK'
    ? 'rgba(16, 185, 129, 0.1)'
    : 'rgba(239, 68, 68, 0.1)'};
  border: 1px solid ${props => props.outcome === 'OUTCOME_OK'
    ? 'rgba(16, 185, 129, 0.3)'
    : 'rgba(239, 68, 68, 0.3)'};
  color: ${props => props.theme.text};
  padding: 10px 12px;
  border-radius: 6px;
  font-family: 'SF Mono', SFMono-Regular, Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 0.85em;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  max-height: 200px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.border}60;
    border-radius: 2px;
  }
`;

const ThinkingSection = styled.div`
  ${props => props.hasToolActivity ? 'border-top: 1px solid ' + (props.theme.border || '#e1e5e9') + '30; padding-top: 15px; margin-top: 5px;' : ''}
`;

const ThinkingSectionHeader = styled.div`
  font-size: 0.9em;
  font-weight: 600;
  color: ${props => props.theme.text}dd;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const formatThinkingElapsed = (elapsedMs) => {
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}:${remainingMinutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};


const getThinkingPreviewFromLatestText = (text = '') => {
  const normalized = String(text || '').replace(/\s+/g, ' ').trim();
  if (!normalized) return '';

  const words = normalized.split(' ');
  const previewWords = words.slice(-18);
  return previewWords.join(' ');
};

const ThinkingTimer = ({ startedAt, completedAt, isStreaming }) => {
  const parsedStart = Date.parse(startedAt);
  const parsedCompleted = Date.parse(completedAt);
  const startTime = Number.isFinite(parsedStart) ? parsedStart : Date.now();
  const endTime = Number.isFinite(parsedCompleted) ? parsedCompleted : null;
  const [now, setNow] = useState(() => endTime || Date.now());

  useEffect(() => {
    if (!isStreaming || endTime) {
      setNow(endTime || Date.now());
      return undefined;
    }

    const intervalId = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [endTime, isStreaming]);

  return (
    <ThinkingTimerBadge aria-label={`Thinking time ${formatThinkingElapsed((endTime || now) - startTime)}`}>
      {formatThinkingElapsed((endTime || now) - startTime)}
    </ThinkingTimerBadge>
  );
};

const ThinkingDropdown = ({
  thinkingContent,
  thinkingPreviewText = '',
  latestThinkingPreviewText = '',
  toolCalls,
  isStreaming = false,
  theme = {},
  showTimer = false,
  thinkingStartedAt,
  thinkingCompletedAt,
  forcePreview = false
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  const hasThinking = thinkingContent && thinkingContent.toString().trim();
  const hasToolActivity = toolCalls && toolCalls.length > 0;

  if (!hasThinking && !hasToolActivity && !forcePreview) {
    return null;
  }

  const getHeaderTitle = () => {
    if (hasThinking && hasToolActivity) return t('chat.thinking.header.thoughtsAndTools');
    if (hasThinking) return t('chat.thinking.header.thoughts');
    if (hasToolActivity) return t('chat.thinking.header.tools');
    return t('chat.thinking.header.thoughts');
  };

  const explicitLatestPreview = typeof latestThinkingPreviewText === 'string'
    ? latestThinkingPreviewText.replace(/\s+/g, ' ').trim()
    : '';
  const normalizedThinkingPreview = explicitLatestPreview || getThinkingPreviewFromLatestText(thinkingPreviewText);
  const thinkingLabel = t('composer.chip.thinking', 'Thinking');
  const headerText = isStreaming
    ? (normalizedThinkingPreview || t('chat.status.thinking'))
    : getHeaderTitle();

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return '[...]';
      case 'executing': return '[~]';
      case 'completed': return '[ok]';
      case 'error': return '[x]';
      default: return '[*]';
    }
  };

  return (
    <ThinkingDropdownContainer>
      <ThinkingHeader onClick={toggleExpanded}>
        {isStreaming && <HeaderSpinner aria-hidden="true" />}
        <ThinkingHeaderTitle>
          <ThinkingPreview>
            {processInlineFormatting(headerText, theme)}
          </ThinkingPreview>
        </ThinkingHeaderTitle>
        {showTimer && (
          <ThinkingTimer
            startedAt={thinkingStartedAt}
            completedAt={thinkingCompletedAt}
            isStreaming={isStreaming}
          />
        )}
        <ThinkingArrow expanded={expanded} aria-hidden="true">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3.5 2L6.5 5L3.5 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </ThinkingArrow>
      </ThinkingHeader>
      <ThinkingContent expanded={expanded}>
        {hasToolActivity && (
          <ToolActivitySection>
            <ToolActivitySectionHeader>{t('chat.thinking.toolActivity')}</ToolActivitySectionHeader>
            {toolCalls.map((toolCall, index) => (
              <ToolActivityItem key={toolCall.tool_id || index}>
                <ToolActivityItemHeader>
                  <ToolActivityIcon>
                    {getStatusIcon(toolCall.status)}
                  </ToolActivityIcon>
                  <ToolActivityName>
                    {toolCall.tool_name || t('chat.thinking.tool.unknown')}
                  </ToolActivityName>
                  <ToolActivityStatus status={toolCall.status}>
                    {t(`chat.thinking.tool.status.${toolCall.status || 'pending'}`, toolCall.status || 'pending')}
                  </ToolActivityStatus>
                </ToolActivityItemHeader>

                {toolCall.parameters && Object.keys(toolCall.parameters).length > 0 && (
                  <ToolActivityDetail>
                    <ToolActivityLabel>{t('chat.thinking.tool.parameters')}</ToolActivityLabel>
                    <ToolActivityValue>
                      {JSON.stringify(toolCall.parameters, null, 2)}
                    </ToolActivityValue>
                  </ToolActivityDetail>
                )}

                {toolCall.result && toolCall.status === 'completed' && (
                  <ToolActivityDetail>
                    <ToolActivityLabel>{t('chat.thinking.tool.result')}</ToolActivityLabel>
                    <ToolActivityValue>
                      {typeof toolCall.result === 'string' ? toolCall.result : JSON.stringify(toolCall.result, null, 2)}
                    </ToolActivityValue>
                  </ToolActivityDetail>
                )}

                {toolCall.error && toolCall.status === 'error' && (
                  <ToolActivityDetail>
                    <ToolActivityLabel>{t('chat.thinking.tool.error')}</ToolActivityLabel>
                    <ToolActivityError>
                      {toolCall.error}
                    </ToolActivityError>
                  </ToolActivityDetail>
                )}
              </ToolActivityItem>
            ))}
          </ToolActivitySection>
        )}

        {hasThinking && (
          <ThinkingSection hasToolActivity={hasToolActivity}>
            {hasToolActivity && <ThinkingSectionHeader>{thinkingLabel}</ThinkingSectionHeader>}
            {thinkingContent}
          </ThinkingSection>
        )}
      </ThinkingContent>
    </ThinkingDropdownContainer>
  );
};

const ChatMessage = ({ message, chat = null, showModelIcons = true, settings = {}, theme = {}, userProfilePicture = null, showProfileIcon = true }) => {
  const { t } = useTranslation();
  const { role, content, timestamp, isError, isLoading, modelId, image, file, sources, type, status, imageUrl, prompt: imagePrompt, flowchartData, id, toolCalls, availableTools, codeExecution, codeExecutionResult, reasoningTrace, reasoningPreviewText, reasoningEffort, thinkingStartedAt, thinkingCompletedAt } = message;
  const { supportedLanguages, isLanguageExecutable } = useSupportedLanguages();
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [deepResearchDetailsOpen, setDeepResearchDetailsOpen] = useState(false);
  const [isSharingChat, setIsSharingChat] = useState(false);
  const [shareStatus, setShareStatus] = useState('');
  const shouldShowThinkingTimer =
    THINKING_PREVIEW_TIMER_ENABLED &&
    isReasoningEffortAboveMedium(reasoningEffort) &&
    Boolean(thinkingStartedAt || timestamp);

  // Debug logging
  if (role === 'assistant' && sources) {
    console.log('[ChatMessage] Message has sources:', sources);
  }

  // Get the prompt for both image and flowchart messages
  const prompt = message.prompt;

  const translateSystemMessage = useCallback((text) => {
    if (!text) return text;
    const trimmed = text.trim();
    if (trimmed.startsWith('API keys are not configured')) {
      return t('chat.errors.apiKeys.notConfigured');
    }
    if (trimmed.startsWith('Add OPENROUTER_API_KEY')) {
      return t('chat.errors.apiKeys.instructions');
    }
    return text;
  }, [t]);

  const normalizedContent = translateSystemMessage(content);

  // Extract sources from content if this is an assistant message and not loading
  const { cleanedContent, sources: extractedSources } = useMemo(() => {
    if (role === 'assistant' && normalizedContent && !isLoading) {
      const result = extractSourcesFromResponse(normalizedContent);
      console.log('[ChatMessage] Extracted sources from content:', result);
      return result;
    }
    return { cleanedContent: content, sources: [] };
  }, [normalizedContent, content, role, isLoading]);

  // Use cleaned content if available, otherwise use original content
  const contentToProcess = cleanedContent || normalizedContent;

  const is3DScene = useMemo(() => {
    if (role !== 'assistant' || isLoading || !content) return false;
    const jsonMatch = content.match(/```json\n([\s\S]*?)\n```/);
    if (!jsonMatch) return false;
    try {
      const parsed = JSON.parse(jsonMatch[1]);
      return Array.isArray(parsed) && parsed.every(obj =>
        obj.id && obj.type && obj.position && obj.rotation && obj.scale
      );
    } catch (e) {
      return false;
    }
  }, [content, role, isLoading]);

  const getAvatar = () => {
    if (role === 'user') {
      if (userProfilePicture) {
        return null;
      }
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
          <circle cx="12" cy="7" r="4"></circle>
        </svg>
      );
    } else if (showModelIcons && modelId) {
      const modelIconProps = {
        modelId,
        size: "small",
        $inMessage: true,
      };

      return <ModelIcon {...modelIconProps} />;
    } else {
      return 'AI';
    }
  };

  // Determine if we should use a model icon (for AI messages with a modelId)
  const useModelIcon = role === 'assistant' && showModelIcons && modelId;

  // Determine alignment preference; default keeps AI left and user right
  const messageAlignmentPreference = settings.messageAlignment || 'default';
  const messageAlignment = messageAlignmentPreference === 'default'
    ? (role === 'assistant' ? 'left' : 'right')
    : (messageAlignmentPreference === 'right' ? 'right' : 'left');
  const shouldRenderAvatar = role !== 'user' || showProfileIcon;

  // Get bubble style from settings
  // Apply high contrast mode if set
  const highContrast = settings.highContrast || false;

  // Check if there's a PDF file attached to the message
  const hasPdfAttachment = file && file.type === 'pdf';

  // Check if there's a text file attached to the message
  const hasTextAttachment = file && file.type === 'text';

  // Function to handle copying message content
  const handleCopyText = () => {
    const textToCopy = cleanedContent || content;
    navigator.clipboard.writeText(textToCopy)
      .then(() => {
        // Could add toast notification here if desired
        console.log('Text copied to clipboard');
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
      });
  };

  // TTS state for toggle
  const [isSpeaking, setIsSpeaking] = useState(false);

  // Function to handle text-to-speech (toggle)
  const handleReadAloud = async () => {
    if (isSpeaking) {
      kokoroTTSService.cancel();
      setIsSpeaking(false);
      return;
    }

    const textToRead = cleanedContent || content;
    if (!textToRead) {
      return;
    }

    try {
      setIsSpeaking(true);
      await kokoroTTSService.speak(textToRead);
    } catch (error) {
      console.error('Kokoro text-to-speech failed:', error);
    } finally {
      setIsSpeaking(false);
    }
  };

  // Ensure TTS state resets if user navigates away or message changes
  useEffect(() => {
    return () => {
      kokoroTTSService.cancel();
      setIsSpeaking(false);
    };
  }, [content, cleanedContent]);


  const showShareStatus = useCallback((message) => {
    setShareStatus(message);
    window.setTimeout(() => setShareStatus(''), 2600);
  }, []);

  const buildShareChatPayload = useCallback(() => {
    if (!chat || !Array.isArray(chat.messages)) {
      return null;
    }

    const currentMessageIndex = chat.messages.findIndex((chatMessage) => chatMessage?.id === id);
    const messages = currentMessageIndex >= 0
      ? chat.messages.slice(0, currentMessageIndex + 1)
      : chat.messages;

    return {
      ...chat,
      messages
    };
  }, [chat, id]);

  const handleShareChat = useCallback(async () => {
    if (isSharingChat) return;

    const chatPayload = buildShareChatPayload();
    if (!chatPayload || chatPayload.messages.length === 0) {
      showShareStatus(t('chat.share.unavailable', 'Nothing to share yet'));
      return;
    }

    const confirmed = window.confirm(
      t(
        'chat.share.confirm',
        'Share this chat? Anyone with the link can view the text-only conversation. Uploaded files, images, and hidden file context are removed.'
      )
    );
    if (!confirmed) return;

    setIsSharingChat(true);
    try {
      const result = await createSharedChat(chatPayload);
      const shareUrl = getSharedChatUrl(result);
      const copied = await copyToClipboard(shareUrl);

      if (copied) {
        showShareStatus(t('chat.share.copied', 'Share link copied'));
      } else {
        window.prompt(t('chat.share.copyManual', 'Copy this share link:'), shareUrl);
        showShareStatus(t('chat.share.copyShown', 'Share link ready'));
      }
    } catch (error) {
      console.error('Failed to share chat:', error);
      showShareStatus(error.message || t('chat.share.failed', 'Share failed'));
    } finally {
      setIsSharingChat(false);
    }
  }, [buildShareChatPayload, isSharingChat, showShareStatus, t]);

  const handleExportDeepResearchPdf = useCallback(async () => {
    if (!contentToProcess || status !== 'completed' || isExportingPdf) return;
    setIsExportingPdf(true);
    try {
      await exportDeepResearchPdf({
        query: message?.query || content || 'Deep Research Report',
        content: contentToProcess,
        metadata: message?.metadata || {},
        sources: Array.isArray(extractedSources) && extractedSources.length > 0
          ? extractedSources
          : (Array.isArray(sources) ? sources : []),
        subQuestions: Array.isArray(message?.subQuestions) ? message.subQuestions : [],
        timestamp
      });
    } catch (error) {
      console.error('Failed to export deep research PDF:', error);
      window.alert('Unable to generate PDF right now. Please try again.');
    } finally {
      setIsExportingPdf(false);
    }
  }, [contentToProcess, status, isExportingPdf, message, content, extractedSources, sources, timestamp]);

  // Determine if the message has sources to display
  const displaySources = extractedSources.length > 0 ? extractedSources : (Array.isArray(sources) ? sources : []);
  const hasSources = role === 'assistant' && displaySources.length > 0;

  console.log('[ChatMessage] Display sources:', {
    extractedSources,
    propsSources: sources,
    displaySources,
    hasSources,
    isLoading,
    role
  });

  // Extract domain from URL for displaying source name and favicon
  const extractDomain = (url) => {
    try {
      const domain = new URL(url).hostname.replace('www.', '');
      return domain;
    } catch (e) {
      console.error('Error extracting domain:', e);
      return url;
    }
  };

  // Get favicon URL for a domain
  const getFaviconUrl = (url) => {
    try {
      const domain = new URL(url).origin;
      return `${domain}/favicon.ico`;
    } catch (e) {
      return 'https://www.google.com/s2/favicons?domain=' + url;
    }
  };

  // Handle generated flowchart message type
  if (type === 'generated-flowchart') {
    let generatedFlowchartContent;
    if (status === 'loading') {
      generatedFlowchartContent = (
        <ThinkingContainer>
          <SpinnerIcon />
          {t('chat.flowchart.creating', { prompt: prompt || t('chat.labels.yourRequest') })}
        </ThinkingContainer>
      );
    } else if (status === 'completed' && flowchartData) {
      generatedFlowchartContent = (
        <>
          {prompt && (
            <p style={{ margin: '0 0 8px 0', opacity: 0.85, fontSize: '0.9em' }}>
              {t('chat.labels.request')}: "{prompt || t('chat.labels.yourRequest')}"
            </p>
          )}
          <FlowchartContainer>
            <FlowchartButton onClick={() => window.dispatchEvent(new CustomEvent('openFlowchartModal', { detail: { flowchartData } }))}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="18" r="3"></circle>
                <circle cx="6" cy="6" r="3"></circle>
                <circle cx="18" cy="6" r="3"></circle>
                <path d="M18 9v1a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V9"></path>
                <path d="M12 12v3"></path>
              </svg>
              {t('chat.flowchart.openBuilder')}
            </FlowchartButton>
            <FlowchartPreview>
              <p style={{ fontSize: '0.9em', opacity: 0.7 }}>{t('chat.flowchart.instructionsHint', { buttonLabel: t('chat.flowchart.openBuilder') })}</p>
            </FlowchartPreview>
          </FlowchartContainer>
        </>
      );
    } else if (status === 'error') {
      generatedFlowchartContent = (
        <div>
          <p style={{ fontWeight: 'bold', color: '#dc3545', marginBottom: '4px' }}>
            {t('chat.flowchart.failed')}
          </p>
          {prompt && <p style={{ margin: '4px 0', opacity: 0.85 }}>{t('chat.labels.request')}: "{prompt || t('chat.labels.yourRequest')}"</p>}
          {content && <p style={{ margin: '4px 0', opacity: 0.85 }}>{t('chat.labels.error')}: {content}</p>}
        </div>
      );
    }

    return (
      <Message $alignment={messageAlignment}>
        {role !== 'user' && (
          <Avatar
            role={role}
            $alignment={messageAlignment}
            $useModelIcon={useModelIcon}
            $profilePicture={role === 'user' ? userProfilePicture : null}
          >
            {getAvatar()}
          </Avatar>
        )}
        <MessageWrapper role={role} $alignment={messageAlignment}>
          <Content role={role} $alignment={messageAlignment} className={`chat-message chat-message--${role}`}>
            {generatedFlowchartContent}
            {timestamp && settings.showTimestamps && (status === 'completed' || status === 'error') && (
              <MessageActions role={role} $alignment={messageAlignment}>
                <Timestamp>{formatTimestamp(timestamp)}</Timestamp>
                {status === 'completed' && flowchartData && (
                  <>
                    <div style={{ flexGrow: 1 }}></div>
                    <ActionButton onClick={() => navigator.clipboard.writeText(flowchartData).then(() => console.log('Flowchart data copied'))}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                      </svg>
                      {t('chat.actions.copyInstructions')}
                    </ActionButton>
                  </>
                )}
              </MessageActions>
            )}
          </Content>
        </MessageWrapper>
      </Message>
    );
  }

  // Handle deep research message type
  if (type === 'deep-research') {
    const queryLabel = message.query || content || t('chat.labels.yourQuery');
    const progressPercent = typeof message.progress === 'number' ? message.progress : null;
    const agentCount = message.metadata?.agentCount
      ?? (Array.isArray(message.agentResults) ? message.agentResults.length : null)
      ?? (Array.isArray(message.subQuestions) ? message.subQuestions.length : null);
    const reportLength = message.metadata?.reportLength || 'standard';
    const reportDepth = message.metadata?.reportDepth || 'standard';

    let deepResearchContent;
    if (status === 'loading') {
      const step = message.statusMessage || t('chat.research.initializing');
      deepResearchContent = (
        <DRCard>
          <DRLoadingBody>
            <DROrbit>
              <span className="ring" />
              <span className="ring inner" />
              <span className="track"><span className="orb" /></span>
              <span className="track reverse"><span className="orb" /></span>
              <span className="track slow"><span className="orb" /></span>
              <div className="core">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="7" />
                  <path d="M20 20l-3.5-3.5" />
                </svg>
              </div>
            </DROrbit>
            <DRLoadingText>
              <div className="title">
                {progressPercent !== null && progressPercent > 0
                  ? `Researching · ${Math.min(99, Math.round(progressPercent))}%`
                  : t('chat.research.performing')}
              </div>
              <div className="step">{step}</div>
              <DRShimmerBar $progress={progressPercent || 12}>
                <div className="fill" />
              </DRShimmerBar>
              <div className="query">"{queryLabel}"</div>
            </DRLoadingText>
          </DRLoadingBody>
        </DRCard>
      );
    } else if (status === 'completed' && content) {
      const metaLine = [
        agentCount ? `${agentCount} agent${agentCount === 1 ? '' : 's'}` : null,
        `${reportLength} · ${reportDepth}`,
        displaySources.length > 0 ? `${displaySources.length} source${displaySources.length === 1 ? '' : 's'}` : null
      ].filter(Boolean).join(' · ');

      const hasQualityIssues = Array.isArray(message.qualityIssues) && message.qualityIssues.length > 0;
      const hasSubQuestions = Array.isArray(message.subQuestions) && message.subQuestions.length > 0;
      const hasModels = !!message.metadata?.models;

      deepResearchContent = (
        <DRCard>
          <DRResultBadgeRow>
            <DRBadge>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2l2.5 6.5L21 11l-5 3.5L17.5 21 12 17l-5.5 4L8 14.5 3 11l6.5-2.5z" />
              </svg>
              Deep Research
            </DRBadge>
            {metaLine && <DRMetaInline>{metaLine}</DRMetaInline>}
            {(hasSubQuestions || hasModels || hasQualityIssues) && (
              <DRDetailsToggle
                $open={deepResearchDetailsOpen}
                onClick={() => setDeepResearchDetailsOpen((v) => !v)}
                aria-expanded={deepResearchDetailsOpen}
              >
                {deepResearchDetailsOpen ? 'Hide details' : 'Research details'}
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </DRDetailsToggle>
            )}
          </DRResultBadgeRow>

          {deepResearchDetailsOpen && (
            <DRDetailsPanel>
              {hasModels && (
                <div className="dr-details-section">
                  <div className="dr-details-label">Pipeline</div>
                  <div className="dr-details-row">
                    <div className="dr-kv"><span className="k">Planner</span><span className="v">{message.metadata.models.planner || '—'}</span></div>
                    <div className="dr-kv"><span className="k">Researcher</span><span className="v">{message.metadata.models.researcher || '—'}</span></div>
                    <div className="dr-kv"><span className="k">Writer</span><span className="v">{message.metadata.models.writer || '—'}</span></div>
                    <div className="dr-kv"><span className="k">Length</span><span className="v" style={{ textTransform: 'capitalize' }}>{reportLength}</span></div>
                    <div className="dr-kv"><span className="k">Depth</span><span className="v" style={{ textTransform: 'capitalize' }}>{reportDepth}</span></div>
                    <div className="dr-kv"><span className="k">Agents</span><span className="v">{agentCount ?? '—'}</span></div>
                  </div>
                </div>
              )}
              {hasSubQuestions && (
                <div className="dr-details-section">
                  <div className="dr-details-label">Research plan ({message.subQuestions.length})</div>
                  <ul className="dr-sub-list">
                    {message.subQuestions.map((question, index) => (
                      <li key={`sq-${index}`}>{question}</li>
                    ))}
                  </ul>
                </div>
              )}
              {hasQualityIssues && (
                <div className="dr-details-section">
                  <div className="dr-details-label">Quality notes</div>
                  <div className="dr-quality">{message.qualityIssues.join(' · ')}</div>
                </div>
              )}
            </DRDetailsPanel>
          )}

          <DRBody>
            {robustFormatContent(contentToProcess, isLanguageExecutable, supportedLanguages, theme)}
          </DRBody>

          {status === 'completed' && content && (
            <DRActionBar>
              <DRActionPill
                $primary
                onClick={handleExportDeepResearchPdf}
                disabled={isExportingPdf}
                title="Export this deep research report as a branded PDF"
              >
                {isExportingPdf ? (
                  <DRSpinIcon xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12a9 9 0 1 1-6.2-8.5" />
                  </DRSpinIcon>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <path d="M9 15v-3" />
                    <path d="M12 15v-4" />
                    <path d="M15 15v-2" />
                  </svg>
                )}
                {isExportingPdf ? 'Exporting…' : 'Export PDF'}
              </DRActionPill>
              <DRActionPill
                onClick={() => navigator.clipboard.writeText(content).then(() => console.log('Research results copied'))}
                title="Copy the report text"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
                Copy
              </DRActionPill>
              {displaySources.length > 0 && (
                <DRActionPill
                  onClick={() => {
                    if (!deepResearchDetailsOpen) setDeepResearchDetailsOpen(true);
                    const first = displaySources[0];
                    if (first?.url) openExternalUrl(first.url);
                  }}
                  title={`View top source (${displaySources.length} total)`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                  </svg>
                  {displaySources.length} sources
                </DRActionPill>
              )}
            </DRActionBar>
          )}
        </DRCard>
      );
    } else if (status === 'error') {
      deepResearchContent = (
        <DRCard>
          <p style={{ fontWeight: 600, color: '#dc2626', margin: '0 0 6px', fontSize: '0.92rem' }}>
            {t('chat.research.failed')}
          </p>
          <p style={{ margin: '4px 0', opacity: 0.85, fontSize: '0.85rem' }}>{t('chat.labels.query')}: "{queryLabel}"</p>
          {(message.errorMessage || message.content) && (
            <p style={{ margin: '4px 0', opacity: 0.85, fontSize: '0.85rem' }}>
              {t('chat.labels.error')}: {message.errorMessage || message.content}
            </p>
          )}
        </DRCard>
      );
    }

    return (
      <Message $alignment={messageAlignment}>
        {role !== 'user' && (
          <Avatar
            role={role}
            $alignment={messageAlignment}
            $useModelIcon={useModelIcon}
            $profilePicture={role === 'user' ? userProfilePicture : null}
          >
            {getAvatar()}
          </Avatar>
        )}
        <MessageWrapper role={role} $alignment={messageAlignment}>
          <Content role={role} $alignment={messageAlignment} className={`chat-message chat-message--${role}`}>
            {deepResearchContent}
            {/* Show sources chips below the card (unchanged behavior) */}
            {hasSources && status === 'completed' && (
              <SourcesContainer>
                {displaySources.map((source, index) => (
                  <SourceButton
                    key={index}
                    onClick={() => openExternalUrl(source.url)}
                    title={source.title}
                  >
                    <SourceFavicon
                      src={getFaviconUrl(source.url)}
                      alt=""
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <span style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {source.title || extractDomain(source.url)}
                    </span>
                  </SourceButton>
                ))}
              </SourcesContainer>
            )}
            {timestamp && settings.showTimestamps && (status === 'completed' || status === 'error') && (
              <MessageActions role={role} $alignment={messageAlignment}>
                <Timestamp>{formatTimestamp(timestamp)}</Timestamp>
              </MessageActions>
            )}
          </Content>
        </MessageWrapper>
      </Message>
    );
  }

  // Handle generated image message type
  if (type === 'generated-image') {
    let generatedImageContent;
    if (status === 'loading') {
      generatedImageContent = (
        <ThinkingContainer>
          <SpinnerIcon />
          {t('chat.image.generating', { prompt: imagePrompt || t('chat.labels.yourPrompt') })}
        </ThinkingContainer>
      );
    } else if (status === 'completed' && imageUrl) {
      generatedImageContent = (
        <>
          {imagePrompt && (
            <p style={{ margin: '0 0 8px 0', opacity: 0.85, fontSize: '0.9em' }}>
              {t('chat.labels.prompt')}: "{imagePrompt}"
            </p>
          )}
          <MessageImage src={imageUrl} alt={imagePrompt || 'Generated AI image'} style={{ maxHeight: '400px' }} />
        </>
      );
    } else if (status === 'error') {
      generatedImageContent = (
        <div>
          <p style={{ fontWeight: 'bold', color: '#dc3545', marginBottom: '4px' }}>
            {t('chat.image.failed')}
          </p>
          {imagePrompt && <p style={{ margin: '4px 0', opacity: 0.85 }}>{t('chat.labels.prompt')}: "{imagePrompt}"</p>}
          {content && <p style={{ margin: '4px 0', opacity: 0.85 }}>{t('chat.labels.error')}: {content}</p>}
        </div>
      );
    }

    return (
      <Message $alignment={messageAlignment}>
        {role !== 'user' && (
          <Avatar
            role={role}
            $alignment={messageAlignment}
            $useModelIcon={useModelIcon}
            $profilePicture={role === 'user' ? userProfilePicture : null}
          >
            {getAvatar()}
          </Avatar>
        )}
        <MessageWrapper role={role} $alignment={messageAlignment}>
          <Content
            role={role}
            $alignment={messageAlignment}
            className={`chat-message chat-message--${role}${highContrast ? ' high-contrast' : ''}`}
          >
            {generatedImageContent}
            {timestamp && settings.showTimestamps && (status === 'completed' || status === 'error') && (
              <MessageActions role={role} $alignment={messageAlignment}>
                <Timestamp>{formatTimestamp(timestamp)}</Timestamp>
                {status === 'completed' && imageUrl && (
                  <>
                    <div style={{ flexGrow: 1 }}></div>
                    <ActionButton onClick={() => navigator.clipboard.writeText(imageUrl).then(() => console.log('Image URL copied'))}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                      </svg>
                      {t('chat.actions.copyUrl')}
                    </ActionButton>
                  </>
                )}
              </MessageActions>
            )}
          </Content>
        </MessageWrapper>
      </Message>
    );
  }

  // Handle generated video message type
  if (type === 'generated-video') {
    const { videoUrl, videoId } = message;
    let generatedVideoContent;
    if (status === 'loading') {
      generatedVideoContent = (
        <ThinkingContainer>
          <SpinnerIcon />
          {t('chat.video.generating', { prompt: imagePrompt || t('chat.labels.yourPrompt') })}
        </ThinkingContainer>
      );
    } else if (status === 'completed' && (videoUrl || videoId)) {
      generatedVideoContent = (
        <>
          {imagePrompt && (
            <p style={{ margin: '0 0 8px 0', opacity: 0.85, fontSize: '0.9em' }}>
              {t('chat.labels.prompt')}: "{imagePrompt}"
            </p>
          )}
          {videoUrl ? (
            <MessageVideo controls src={videoUrl} style={{ maxHeight: '400px' }} />
          ) : (
            <p style={{ margin: 0, opacity: 0.85 }}>
              {t('chat.video.previewUnavailable', 'Preview unavailable. Download the video to view it.')}
            </p>
          )}
        </>
      );
    } else if (status === 'error') {
      generatedVideoContent = (
        <div>
          <p style={{ fontWeight: 'bold', color: '#dc3545', marginBottom: '4px' }}>
            {t('chat.video.failed')}
          </p>
          {imagePrompt && <p style={{ margin: '4px 0', opacity: 0.85 }}>{t('chat.labels.prompt')}: "{imagePrompt}"</p>}
          {content && <p style={{ margin: '4px 0', opacity: 0.85 }}>{t('chat.labels.error')}: {content}</p>}
        </div>
      );
    }

    return (
      <Message $alignment={messageAlignment}>
        {role !== 'user' && (
          <Avatar
            role={role}
            $alignment={messageAlignment}
            $useModelIcon={useModelIcon}
            $profilePicture={role === 'user' ? userProfilePicture : null}
          >
            {getAvatar()}
          </Avatar>
        )}
        <MessageWrapper role={role} $alignment={messageAlignment}>
          <Content
            role={role}
            $alignment={messageAlignment}
            className={`chat-message chat-message--${role}${highContrast ? ' high-contrast' : ''}`}
          >
            {generatedVideoContent}
            {timestamp && settings.showTimestamps && (status === 'completed' || status === 'error') && (
              <MessageActions role={role} $alignment={messageAlignment}>
                <Timestamp>{formatTimestamp(timestamp)}</Timestamp>
                {status === 'completed' && videoId && (
                  <>
                    <div style={{ flexGrow: 1 }}></div>
                    <ActionButton onClick={() => downloadGeneratedVideo(videoId).catch((error) => console.error('Video download failed', error))}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      {t('chat.actions.download', 'Download')}
                    </ActionButton>
                  </>
                )}
              </MessageActions>
            )}
          </Content>
        </MessageWrapper>
      </Message>
    );
  }

  return (
    <Message $alignment={messageAlignment}>
      {shouldRenderAvatar && (
        <Avatar
          role={role}
          $alignment={messageAlignment}
          $useModelIcon={useModelIcon}
          $profilePicture={role === 'user' ? userProfilePicture : null}
        >
          {getAvatar()}
        </Avatar>
      )}
      {isError ? (
        <ErrorMessage role={role} $alignment={messageAlignment} className={`chat-message chat-message--${role}`}>
          {content}
          {timestamp && settings.showTimestamps && <Timestamp>{formatTimestamp(timestamp)}</Timestamp>}
        </ErrorMessage>
      ) : (
        <MessageWrapper role={role} $alignment={messageAlignment}>
          <Content
            role={role}
            $alignment={messageAlignment}
            className={`chat-message chat-message--${role}${highContrast ? ' high-contrast' : ''}`}
          >
            {image && (
              <MessageImage src={image} alt="Uploaded image" />
            )}
            {hasPdfAttachment && (
              <FileAttachmentContainer>
                <FileIcon style={{ color: '#e64a3b' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <path d="M9 15h6"></path>
                    <path d="M9 11h6"></path>
                  </svg>
                </FileIcon>
                <FileName>{file.name || 'document.pdf'}</FileName>
              </FileAttachmentContainer>
            )}
            {hasTextAttachment && (
              <FileAttachmentContainer>
                <FileIcon style={{ color: '#4285F4' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10 9 9 9 8 9"></polyline>
                  </svg>
                </FileIcon>
                <FileName>{file.name || 'document.txt'}</FileName>
              </FileAttachmentContainer>
            )}
            {(() => {
              // If loading and no content yet, show thinking indicator
              if (
                isLoading &&
                !content &&
                !(typeof reasoningTrace === 'string' && reasoningTrace.trim()) &&
                !(toolCalls && toolCalls.length > 0)
              ) {
                if (shouldShowThinkingTimer) {
                  return (
                    <ThinkingDropdown
                      thinkingContent={null}
                      thinkingPreviewText=""
                      toolCalls={toolCalls}
                      isStreaming={isLoading}
                      theme={theme}
                      showTimer={shouldShowThinkingTimer}
                      thinkingStartedAt={thinkingStartedAt || timestamp}
                      thinkingCompletedAt={thinkingCompletedAt}
                      forcePreview
                    />
                  );
                }

                return (
                  <ThinkingContainer>
                    <SpinnerIcon />
                    {t('chat.status.thinking')}
                  </ThinkingContainer>
                );
              }

              // Process content and show main content + thinking dropdown if applicable
              const { mainContent: mainContentWithoutThinking, thinkingContent: rawThinkingContent } =
                extractThinkingFromContent(contentToProcess);
              const combinedThinkingText = [reasoningTrace, rawThinkingContent]
                .map(value => (typeof value === 'string' ? value.trim() : ''))
                .filter(Boolean)
                .filter((value, index, array) => array.indexOf(value) === index)
                .join('\n\n');
              const formattedThinkingContent = combinedThinkingText
                ? processText(combinedThinkingText, true, isLanguageExecutable, supportedLanguages, theme)
                : null;

              if (formattedThinkingContent) {
                // If content has reasoning/thinking traces, show the collapsible trace first.
                return (
                  <>
                    <ThinkingDropdown
                      thinkingContent={formattedThinkingContent}
                      thinkingPreviewText={combinedThinkingText}
                      latestThinkingPreviewText={isLoading ? reasoningPreviewText : ''}
                      toolCalls={toolCalls}
                      isStreaming={isLoading}
                      theme={theme}
                      showTimer={shouldShowThinkingTimer}
                      thinkingStartedAt={thinkingStartedAt || timestamp}
                      thinkingCompletedAt={thinkingCompletedAt}
                    />
                    <StreamingMarkdownRenderer
                      text={mainContentWithoutThinking}
                      isStreaming={isLoading}
                      theme={theme}
                    />
                  </>
                );
              }
              // If content has no thinking tags, but may have tool activity or code execution
              const hasToolActivity = toolCalls && toolCalls.length > 0;
              const hasCodeExecution = codeExecution || codeExecutionResult;

              return (
                <>
                  {!hasToolActivity && shouldShowThinkingTimer && isLoading && (
                    <ThinkingDropdown
                      thinkingContent={null}
                      thinkingPreviewText=""
                      toolCalls={toolCalls}
                      isStreaming={isLoading}
                      theme={theme}
                      showTimer={shouldShowThinkingTimer}
                      thinkingStartedAt={thinkingStartedAt || timestamp}
                      thinkingCompletedAt={thinkingCompletedAt}
                      forcePreview
                    />
                  )}
                  {hasToolActivity && (
                    <ThinkingDropdown
                      thinkingContent={null}
                      thinkingPreviewText=""
                      toolCalls={toolCalls}
                      isStreaming={isLoading}
                      theme={theme}
                      showTimer={shouldShowThinkingTimer}
                      thinkingStartedAt={thinkingStartedAt || timestamp}
                      thinkingCompletedAt={thinkingCompletedAt}
                    />
                  )}

                  {/* Code Execution Display (Gemini) */}
                  {hasCodeExecution && (
                    <CodeExecutionSection>
                      <CodeExecutionHeader>
                        <CodeExecutionIcon>
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="16 18 22 12 16 6"></polyline>
                            <polyline points="8 6 2 12 8 18"></polyline>
                          </svg>
                        </CodeExecutionIcon>
                        Code Execution
                      </CodeExecutionHeader>
                      <CodeExecutionBody>
                        {codeExecution && (
                          <>
                            <div style={{ fontSize: '0.75em', color: 'inherit', opacity: 0.7, marginBottom: '6px' }}>
                              {codeExecution.language || 'python'}
                            </div>
                            <CodeExecutionCode>
                              {codeExecution.code}
                            </CodeExecutionCode>
                          </>
                        )}
                        {codeExecutionResult && (
                          <CodeExecutionResultSection>
                            <CodeExecutionResultHeader outcome={codeExecutionResult.outcome}>
                              {codeExecutionResult.outcome === 'OUTCOME_OK' ? (
                                <>
                                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                  </svg>
                                  Output
                                </>
                              ) : (
                                <>
                                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="15" y1="9" x2="9" y2="15"></line>
                                    <line x1="9" y1="9" x2="15" y2="15"></line>
                                  </svg>
                                  Error
                                </>
                              )}
                            </CodeExecutionResultHeader>
                            <CodeExecutionOutput outcome={codeExecutionResult.outcome}>
                              {codeExecutionResult.output || 'No output'}
                            </CodeExecutionOutput>
                          </CodeExecutionResultSection>
                        )}
                      </CodeExecutionBody>
                    </CodeExecutionSection>
                  )}

                  <StreamingMarkdownRenderer
                    text={mainContentWithoutThinking}
                    isStreaming={isLoading}
                    theme={theme}
                  />
                </>
              );
            })()}
          </Content>


          {/* Sources section */}
          {hasSources && !isLoading && (
            <SourcesContainer>
              {displaySources.map((source, index) => (
                <SourceButton key={`source-${index}`} onClick={() => openExternalUrl(source.url)}>
                  <SourceFavicon src={getFaviconUrl(source.url)} alt="" onError={(e) => e.target.src = 'https://www.google.com/s2/favicons?domain=' + source.url} />
                  {source.domain || extractDomain(source.url)}
                </SourceButton>
              ))}
            </SourcesContainer>
          )}

          {/* Message action buttons - only show for completed AI messages (not loading) */}
          {!isLoading && contentToProcess && role === 'assistant' && (
            <MessageActions role={role} $alignment={messageAlignment}>
              {timestamp && settings.showTimestamps && <Timestamp>{formatTimestamp(timestamp)}</Timestamp>}
              <div style={{ flexGrow: 1 }}></div>
              {role === 'assistant' && (
                <ActionButton onClick={() => window.location.reload()}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10"></polyline>
                    <polyline points="1 20 1 14 7 14"></polyline>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                  </svg>
                </ActionButton>
              )}
              <ActionButton onClick={handleCopyText}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
              </ActionButton>
              <ActionButton
                onClick={handleShareChat}
                disabled={isSharingChat || !chat}
                aria-label={t('chat.share.action', 'Share chat')}
                title={shareStatus || t('chat.share.action', 'Share chat')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="18" cy="5" r="3"></circle>
                  <circle cx="6" cy="12" r="3"></circle>
                  <circle cx="18" cy="19" r="3"></circle>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                </svg>
              </ActionButton>
              {shareStatus && <Timestamp role="status" aria-live="polite">{shareStatus}</Timestamp>}
              {role === 'assistant' && (
                <>
                  <ActionButton onClick={() => console.log('Thumbs up')}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
                    </svg>
                  </ActionButton>
                  <ActionButton onClick={() => console.log('Thumbs down')}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"></path>
                    </svg>
                  </ActionButton>
                </>
              )}
              <ActionButton onClick={handleReadAloud} title={isSpeaking ? t('chat.actions.stopSpeaking', 'Stop speaking') : t('chat.actions.readAloud', 'Read aloud')}>
                {isSpeaking ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="6" y="6" width="12" height="12" rx="2" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 5L6 9H2v6h4l5 4V5z"></path>
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path>
                  </svg>
                )}
              </ActionButton>
              <ActionButton onClick={() => console.log('More options')}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="1"></circle>
                  <circle cx="19" cy="12" r="1"></circle>
                  <circle cx="5" cy="12" r="1"></circle>
                </svg>
              </ActionButton>
              {is3DScene && (
                <ActionButton onClick={() => {
                  const jsonMatch = contentToProcess.match(/```json\n([\s\S]*?)\n```/);
                  if (jsonMatch) {
                    try {
                      const parsed = JSON.parse(jsonMatch[1]);
                      if (Array.isArray(parsed)) {
                        window.dispatchEvent(new CustomEvent('load3DScene', { detail: { objects: parsed } }));
                      }
                    } catch (e) {
                      console.error('Failed to parse 3D scene JSON', e);
                    }
                  }
                }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"></path>
                  </svg>
                  {t('chat.actions.load3d', 'Load in 3D')}
                </ActionButton>
              )}
            </MessageActions>
          )}
        </MessageWrapper>
      )}
    </Message>
  );
};

export default ChatMessage;
